import React from 'lib/react';
import { useTranslation } from 'lib/translation';
import { useSelector } from 'lib/redux';
import { EVGrid } from 'lib/components';
import Typography from 'commonComponents/typography';
import AnalyticsBarChart from 'commonComponents/analyticsBarChart';
import { markElementClasses } from '@mui/x-charts';
import { AVG_TURN_COLORS, AVG_TURN_LINE_COLOR, STATUS_LABELS } from '../Analytics.constants';
import ChartsStyles from './ChartsStyles';
import LastUpdatedTime from './LastUpdatedTime.component';
import BarChartLegend from './BarChartLegend.component';

function AvgTurnCharts() {
  const classes = ChartsStyles();
  const { t } = useTranslation();
  const reportAnalytics = useSelector(({ analyticsDashboard }) => analyticsDashboard.reportAnalytics);

  const reportsAvgTurnData = Object.entries(reportAnalytics.reduce(
    (acc, item) => {
      const {
        currentStatus, orderPlacedDate, turnaroundBusinessHours, turnaroundCalendarHours,
      } = item;
      const month = new Date(orderPlacedDate).getMonth();
      const monthData = acc[month] || { };
      const statusCount = monthData[currentStatus] || 0;
      const currentTurnAroundBusinessHours = monthData.totalTurnAroundBusinessHours || 0;
      const currentTurnAroundCalendarHours = monthData.totalTurnAroundCalendarHours || 0;
      return {
        ...acc,
        [month]: {
          ...monthData,
          [currentStatus]: statusCount + 1,
          totalTurnAroundBusinessHours: currentTurnAroundBusinessHours + (currentStatus === STATUS_LABELS.Completed ? turnaroundBusinessHours : 0),
          totalTurnAroundCalendarHours: currentTurnAroundCalendarHours + (currentStatus === STATUS_LABELS.Completed ? turnaroundCalendarHours : 0),
        },
      };
    },
    {},
  )).map(([month, monthData]) => {
    const avgTurnAroundBusinessHours = monthData.Completed ? Number((monthData.totalTurnAroundBusinessHours / monthData.Completed).toFixed(2)) : 0;
    const avgTurnAroundCalendarHours = monthData.Completed ? Number((monthData.totalTurnAroundCalendarHours / monthData.Completed).toFixed(2)) : 0;
    return {
      ...monthData, month: Number(month) + 1, avgTurnAroundBusinessHours, avgTurnAroundCalendarHours,
    };
  });

  const avgTurnBarChartSeries = Object.entries(AVG_TURN_COLORS).map(([status, color]) => ({
    dataKey: status,
    stack: 'reportsCount',
    color,
    label: status,
    valueFormatter: (value) => `${value} Reports`,
  }));

  avgTurnBarChartSeries.push({
    id: 'businessHours',
    type: 'line',
    dataKey: 'avgTurnAroundBusinessHours',
    yAxisId: 'rightAxis',
    curve: 'linear',
    color: AVG_TURN_LINE_COLOR,
    label: 'Business Hours',
    valueFormatter: (value) => `${value} hrs`,
  });
  avgTurnBarChartSeries.push({
    id: 'calendarHours',
    type: 'line',
    dataKey: 'avgTurnAroundCalendarHours',
    yAxisId: 'rightAxis',
    curve: 'linear',
    color: AVG_TURN_LINE_COLOR,
    label: 'Calendar Hours',
    valueFormatter: (value) => `${value} hrs`,
  });

  return (
    <EVGrid className={classes.barChartContainer}>
      <Typography variant="h3-600" fontColor="charcoal-900">{t('analytics_dashboard__average_turnaround_time')}</Typography>
      <EVGrid className={classes.barChartDiv}>
        <AnalyticsBarChart
          dataset={reportsAvgTurnData}
          series={avgTurnBarChartSeries}
          xAxis={[{
            scaleType: 'band',
            dataKey: 'month',
            categoryGapRatio: 0.05,
            disableTicks: true,
            valueFormatter: (monthNum) => new Date(2021, monthNum - 1).toLocaleString('default', { month: 'short' }).slice(0, 3).toUpperCase(),
          },
          ]}
          yAxis={[
            {
              id: 'leftAxis',
              disableTicks: true,
              label: t('analytics_dashboard__volume'),
              valueFormatter: (volume) => Number(volume),
            },
            {
              id: 'rightAxis',
              disableTicks: true,
              label: t('analytics_dashboard__avg_turn_hrs_label'),
              valueFormatter: (avgTurn) => avgTurn,
            },
          ]}
          leftAxis="leftAxis"
          rightAxis="rightAxis"
          sx={{
            '& .MuiLineElement-series-businessHours': {
              strokeDasharray: '4 4',
            },
            [`.${markElementClasses.root}`]: {
              fill: AVG_TURN_LINE_COLOR,
            },
            '& .MuiChartsAxis-right': {
              stroke: '#288301',
              strokeWidth: 0.5,
              '& .MuiChartsAxis-label': {
                transform: 'translateX(64px) rotate(180deg)',
                transformOrigin: 'left',
              },
            },
          }}
        />
      </EVGrid>
      <EVGrid className={classes.legend}>
        <BarChartLegend
          lengendData={[
            ...Object.entries(AVG_TURN_COLORS).map(([status, color]) => ({ label: status, color })),
            { label: t('analytics_dashboard__calendar_hours'), color: AVG_TURN_LINE_COLOR, type: 'solidLine' },
            { label: t('analytics_dashboard__business_hours'), color: AVG_TURN_LINE_COLOR, type: 'dashedLine' },
          ]}
        />
        <LastUpdatedTime />
      </EVGrid>
    </EVGrid>
  );
}

export default AvgTurnCharts;
